import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  formatFeet,
  optimizeRackLayout,
  polygonArea,
  polygonBounds,
  rectanglePolygon,
} from "./rackLayoutEngine";
import {
  canAccessDatacenterLayout,
  datacenterLayoutPluginEnabled,
} from "./datacenterLayoutAccess";
import {
  createDatacenterLayout,
  deleteDatacenterLayout,
  getDatacenterLayout,
  listDatacenterLayouts,
  updateDatacenterLayout,
} from "./datacenterLayoutsApi";
import "./datacenterPage.css";

const DEFAULT_ROOM_W = 40;
const DEFAULT_ROOM_D = 30;

/** Fixed floor-plan world (feet). Viewport zooms within this; it never resizes with the cursor. */
const WORLD_WIDTH_FT = 200;
const WORLD_HEIGHT_FT = 150;
const WORLD_ASPECT = WORLD_WIDTH_FT / WORLD_HEIGHT_FT;
const MIN_VIEW_WIDTH_FT = 10;
const MAX_VIEW_WIDTH_FT = WORLD_WIDTH_FT;

/** TIA-942 preferred front (cold) aisle. */
const DEFAULT_COLD_AISLE_FT = 4;
/** TIA-942 preferred rear (hot) aisle. */
const DEFAULT_HOT_AISLE_FT = 3;
const DEFAULT_WALL_CLEARANCE_FT = 3;
/**
 * TIA-942 does not mandate inter-rack side clearance (cabinets may be flush).
 * Fiber data centers commonly leave ~6" for a vertical cable manager (VCM) between racks.
 */
const DEFAULT_SIDE_GAP_FT = 0.5; // 6 inches

const SIDE_GAP_PRESETS = [
  { id: "flush", label: "Flush (0″)", ft: 0 },
  { id: "vcm6", label: "Fiber VCM 6″", ft: 0.5 },
  { id: "vcm10", label: "Fiber VCM 10″", ft: 10 / 12 },
  { id: "vcm12", label: "Fiber VCM 12″", ft: 1 },
];

const OBSTRUCTION_PRESETS = [
  { id: "pillar", label: "Pillar", w: 2, h: 2 },
  { id: "floor_vent", label: "Floor vent", w: 2, h: 2 },
  { id: "pdu", label: "PDU / power", w: 3, h: 2 },
  { id: "other", label: "Other", w: 2, h: 2 },
];

function newObstructionId() {
  return `obs_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function findNearestObstruction(point, obstructions, maxDist) {
  let best = -1;
  let bestD = maxDist;
  for (let i = 0; i < obstructions.length; i += 1) {
    const o = obstructions[i];
    const cx = o.x + o.w / 2;
    const cy = o.y + o.h / 2;
    const d = Math.hypot(point.x - cx, point.y - cy);
    const inside =
      point.x >= o.x && point.x <= o.x + o.w && point.y >= o.y && point.y <= o.y + o.h;
    if (inside || d <= bestD) {
      bestD = inside ? 0 : d;
      best = i;
      if (inside) break;
    }
  }
  return best;
}

const INITIAL_VIEWBOX = {
  minX: 0,
  minY: 0,
  width: WORLD_WIDTH_FT,
  height: WORLD_HEIGHT_FT,
};

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function clampPointToWorld(p) {
  return {
    x: clamp(p.x, 0, WORLD_WIDTH_FT),
    y: clamp(p.y, 0, WORLD_HEIGHT_FT),
  };
}

function clampPointToWorldRect(rect) {
  const w = Math.max(0.1, rect.w);
  const h = Math.max(0.1, rect.h);
  return {
    ...rect,
    w,
    h,
    x: clamp(rect.x, 0, Math.max(0, WORLD_WIDTH_FT - w)),
    y: clamp(rect.y, 0, Math.max(0, WORLD_HEIGHT_FT - h)),
  };
}

function clampViewBox(vb) {
  let width = clamp(vb.width, MIN_VIEW_WIDTH_FT, MAX_VIEW_WIDTH_FT);
  let height = width / WORLD_ASPECT;
  if (height > WORLD_HEIGHT_FT) {
    height = WORLD_HEIGHT_FT;
    width = height * WORLD_ASPECT;
  }
  const minX = clamp(vb.minX, 0, WORLD_WIDTH_FT - width);
  const minY = clamp(vb.minY, 0, WORLD_HEIGHT_FT - height);
  return { minX, minY, width, height };
}

function snap(value, grid) {
  if (!grid || grid <= 0) return value;
  return Math.round(value / grid) * grid;
}

function clonePoints(points) {
  return (points || []).map((p) => ({ x: p.x, y: p.y }));
}

function dist(a, b) {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function findNearestVertex(point, points, maxDist) {
  let best = -1;
  let bestD = maxDist;
  for (let i = 0; i < points.length; i += 1) {
    const d = dist(point, points[i]);
    if (d <= bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

function distPointToSegment(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-12) return { dist: dist(p, a), t: 0, point: { ...a } };
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const point = { x: a.x + t * dx, y: a.y + t * dy };
  return { dist: dist(p, point), t, point };
}

/** Insert after edgeStartIndex when click is near that edge. */
function findNearestEdgeInsert(point, points, maxDist) {
  if (!points || points.length < 2) return -1;
  let best = -1;
  let bestD = maxDist;
  const closed = points.length >= 3;
  const count = closed ? points.length : points.length - 1;
  for (let i = 0; i < count; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const { dist: d, t } = distPointToSegment(point, a, b);
    if (t <= 0.02 || t >= 0.98) continue; // prefer vertex hit instead
    if (d <= bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

function buildSpanLabels(points, { closed = false, previewTo = null } = {}) {
  const labels = [];
  if (!points?.length) return labels;

  const edgeCount = closed ? points.length : Math.max(0, points.length - 1);
  for (let i = 0; i < edgeCount; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const length = dist(a, b);
    if (length < 1e-6) continue;
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const nx = -dy / length;
    const ny = dx / length;
    const offset = Math.min(1.2, Math.max(0.45, length * 0.08));
    labels.push({
      key: `span-${i}`,
      edgeIndex: i,
      x: mx + nx * offset,
      y: my + ny * offset,
      length,
      preview: false,
    });
  }

  if (previewTo && points.length >= 1) {
    const a = points[points.length - 1];
    const length = dist(a, previewTo);
    if (length >= 1e-6) {
      const mx = (a.x + previewTo.x) / 2;
      const my = (a.y + previewTo.y) / 2;
      const dx = previewTo.x - a.x;
      const dy = previewTo.y - a.y;
      const nx = -dy / length;
      const ny = dx / length;
      const offset = Math.min(1.2, Math.max(0.45, length * 0.08));
      labels.push({
        key: "span-preview",
        x: mx + nx * offset,
        y: my + ny * offset,
        length,
        preview: true,
      });
    }
  }

  return labels;
}

function formatSpanLabel(lengthFt) {
  if (lengthFt >= 10) return `${lengthFt.toFixed(1)} ft`;
  return `${lengthFt.toFixed(2)} ft`;
}

/** Span badge size in world feet (stays fixed while zooming, so labels shrink on screen when zoomed out). */
const SPAN_LABEL_FONT_FT = 1.35;
const SPAN_EDIT_BOX_W_FT = 9;
const SPAN_EDIT_BOX_H_FT = 3.2;

/** Keep vertex `edgeIndex` fixed; move the next vertex along the same direction. */
function resizeEdgeKeepStart(points, edgeIndex, newLengthFt, snapGridFt = 0) {
  const next = clonePoints(points);
  if (!next.length || edgeIndex < 0 || edgeIndex >= next.length) return next;
  const a = next[edgeIndex];
  const bIndex = (edgeIndex + 1) % next.length;
  const b = next[bIndex];
  const len = dist(a, b);
  const L = Math.max(0.1, Number(newLengthFt) || 0.1);
  let ux;
  let uy;
  if (len < 1e-9) {
    ux = 1;
    uy = 0;
  } else {
    ux = (b.x - a.x) / len;
    uy = (b.y - a.y) / len;
  }
  next[bIndex] = clampPointToWorld({
    x: snap(a.x + ux * L, snapGridFt),
    y: snap(a.y + uy * L, snapGridFt),
  });
  return next;
}

function loadImageFileAsBackground(file) {
  return new Promise((resolve, reject) => {
    if (!file || !String(file.type || "").startsWith("image/")) {
      reject(new Error("Choose an image file (PNG, JPG, or WebP)."));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read image file."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not decode image."));
      img.onload = () => {
        const maxPx = 1600;
        const scale = Math.min(1, maxPx / Math.max(img.width, img.height, 1));
        const cw = Math.max(1, Math.round(img.width * scale));
        const ch = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, cw, ch);
        const src = canvas.toDataURL("image/jpeg", 0.72);
        const aspect = ch / cw;
        const worldW = 100;
        const worldH = worldW * aspect;
        resolve({
          src,
          x: 0,
          y: 0,
          w: worldW,
          h: worldH,
          opacity: 0.45,
          visible: true,
        });
      };
      img.src = String(reader.result || "");
    };
    reader.readAsDataURL(file);
  });
}

export function DatacenterPage({ getAuthHeaders } = {}) {
  const auth = getAuthHeaders;
  const [shapeMode, setShapeMode] = useState("rectangle"); // rectangle | polygon
  const [roomW, setRoomW] = useState(DEFAULT_ROOM_W);
  const [roomD, setRoomD] = useState(DEFAULT_ROOM_D);
  const [polygon, setPolygon] = useState(() => rectanglePolygon(DEFAULT_ROOM_W, DEFAULT_ROOM_D));
  const [drawing, setDrawing] = useState(false);
  const [draftPoints, setDraftPoints] = useState([]);
  const [cursorWorld, setCursorWorld] = useState(null);

  const [rackW, setRackW] = useState(2); // 24"
  const [rackD, setRackD] = useState(4); // 48"
  const [wallClearance, setWallClearance] = useState(DEFAULT_WALL_CLEARANCE_FT);
  const [coldAisle, setColdAisle] = useState(DEFAULT_COLD_AISLE_FT);
  const [hotAisle, setHotAisle] = useState(DEFAULT_HOT_AISLE_FT);
  const [sideGap, setSideGap] = useState(DEFAULT_SIDE_GAP_FT);
  const [pattern, setPattern] = useState("auto");
  const [orientation, setOrientation] = useState("auto");
  const [snapGrid, setSnapGrid] = useState(0.5);
  const [viewBox, setViewBox] = useState(INITIAL_VIEWBOX);
  const [obstructions, setObstructions] = useState([]);
  const [obsDraft, setObsDraft] = useState({ type: "pillar", w: 2, h: 2, label: "Pillar" });
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [bgUploadError, setBgUploadError] = useState("");
  const [editingSpan, setEditingSpan] = useState(null); // { edgeIndex, value, x, y }
  const spanInputRef = useRef(null);
  const bgFileRef = useRef(null);

  const [savedId, setSavedId] = useState(null);
  const [savedName, setSavedName] = useState("Untitled layout");
  const [savedList, setSavedList] = useState([]);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [dirty, setDirty] = useState(false);

  const [contextMenu, setContextMenu] = useState(null);
  const [dragVertex, setDragVertex] = useState(null);
  const [dragObs, setDragObs] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const svgRef = useRef(null);
  const svgWrapRef = useRef(null);
  const suppressClickRef = useRef(false);
  const panRef = useRef(null); // { lastX, lastY, pointerId }
  const draftPointsRef = useRef(draftPoints);
  draftPointsRef.current = draftPoints;
  const viewBoxRef = useRef(viewBox);
  viewBoxRef.current = viewBox;

  const markDirty = useCallback(() => setDirty(true), []);

  const activePolygon = drawing ? draftPoints : polygon;
  const polygonEditActive = drawing || shapeMode === "polygon";
  const showSpanLabels = activePolygon.length >= 2;
  const roomBounds = useMemo(
    () => polygonBounds(activePolygon.length ? activePolygon : rectanglePolygon(roomW, roomD)),
    [activePolygon, roomW, roomD]
  );

  const layout = useMemo(() => {
    if (drawing || activePolygon.length < 3) {
      return {
        racks: [],
        sideGaps: [],
        count: 0,
        pattern: "hotCold",
        aisleAlong: "x",
        roomArea: polygonArea(activePolygon),
        rackArea: 0,
        utilization: 0,
        sideGap,
      };
    }
    return optimizeRackLayout({
      polygon: activePolygon,
      rackWidth: rackW,
      rackDepth: rackD,
      wallClearance,
      coldAisle,
      hotAisle,
      sideGap,
      pattern,
      orientation,
      obstructions,
      obstructionClearance: 0,
    });
  }, [
    activePolygon,
    drawing,
    rackW,
    rackD,
    wallClearance,
    coldAisle,
    hotAisle,
    sideGap,
    pattern,
    orientation,
    obstructions,
  ]);

  const collectLayoutData = useCallback(
    () => ({
      shapeMode,
      roomW,
      roomD,
      polygon: clonePoints(polygon),
      rackW,
      rackD,
      wallClearance,
      coldAisle,
      hotAisle,
      sideGap,
      pattern,
      orientation,
      snapGrid,
      obstructions: obstructions.map((o) => ({ ...o })),
      backgroundImage: backgroundImage ? { ...backgroundImage } : null,
    }),
    [
      shapeMode,
      roomW,
      roomD,
      polygon,
      rackW,
      rackD,
      wallClearance,
      coldAisle,
      hotAisle,
      sideGap,
      pattern,
      orientation,
      snapGrid,
      obstructions,
      backgroundImage,
    ]
  );

  const applyLayoutData = useCallback((data) => {
    if (!data || typeof data !== "object") return;
    const mode = data.shapeMode === "polygon" ? "polygon" : "rectangle";
    setShapeMode(mode);
    setRoomW(Math.max(1, Number(data.roomW) || DEFAULT_ROOM_W));
    setRoomD(Math.max(1, Number(data.roomD) || DEFAULT_ROOM_D));
    const poly =
      Array.isArray(data.polygon) && data.polygon.length >= 3
        ? clonePoints(data.polygon)
        : rectanglePolygon(data.roomW || DEFAULT_ROOM_W, data.roomD || DEFAULT_ROOM_D);
    setPolygon(poly);
    setDrawing(false);
    setDraftPoints([]);
    setRackW(Math.max(0.1, Number(data.rackW) || 2));
    setRackD(Math.max(0.1, Number(data.rackD) || 4));
    setWallClearance(Math.max(0, Number(data.wallClearance) || 0));
    setColdAisle(Math.max(0, Number(data.coldAisle) || 0));
    setHotAisle(Math.max(0, Number(data.hotAisle) || 0));
    setSideGap(Math.max(0, Number(data.sideGap) || 0));
    setPattern(["auto", "hotCold", "single"].includes(data.pattern) ? data.pattern : "auto");
    setOrientation(["auto", "x", "y"].includes(data.orientation) ? data.orientation : "auto");
    setSnapGrid(Math.max(0, Number(data.snapGrid) || 0));
    setObstructions(
      Array.isArray(data.obstructions)
        ? data.obstructions.map((o, i) => ({
            id: o.id || `obs_${i}`,
            x: Number(o.x) || 0,
            y: Number(o.y) || 0,
            w: Math.max(0.1, Number(o.w) || 2),
            h: Math.max(0.1, Number(o.h) || 2),
            label: o.label || o.type || "Obstruction",
            type: o.type || "other",
          }))
        : []
    );
    setBackgroundImage(
      data.backgroundImage && data.backgroundImage.src
        ? {
            src: data.backgroundImage.src,
            x: Number(data.backgroundImage.x) || 0,
            y: Number(data.backgroundImage.y) || 0,
            w: Math.max(1, Number(data.backgroundImage.w) || 100),
            h: Math.max(1, Number(data.backgroundImage.h) || 75),
            opacity: Math.min(1, Math.max(0.05, Number(data.backgroundImage.opacity) || 0.45)),
            visible: data.backgroundImage.visible !== false,
          }
        : null
    );
    setEditingSpan(null);
    setDirty(false);
    setContextMenu(null);
  }, []);

  const refreshSavedList = useCallback(async () => {
    try {
      const list = await listDatacenterLayouts(auth);
      setSavedList(list);
    } catch (err) {
      setSaveError(err?.response?.data?.error || err.message || "Could not load saved layouts.");
    }
  }, [auth]);

  useEffect(() => {
    refreshSavedList();
  }, [refreshSavedList]);

  useEffect(() => {
    if (editingSpan && spanInputRef.current) {
      spanInputRef.current.focus();
      spanInputRef.current.select();
    }
  }, [editingSpan]);

  const commitSpanEdit = useCallback(() => {
    if (!editingSpan) return;
    const edgeIndex = editingSpan.edgeIndex;
    const newLen = Number(editingSpan.value);
    setEditingSpan(null);
    if (!Number.isFinite(newLen) || newLen <= 0) return;

    if (drawing) {
      setDraftPoints((prev) => {
        if (edgeIndex < 0 || edgeIndex >= prev.length - 1) return prev;
        const next = resizeEdgeKeepStart(prev, edgeIndex, newLen, snapGrid);
        draftPointsRef.current = next;
        return next;
      });
      markDirty();
      return;
    }

    setPolygon((prev) => {
      if (edgeIndex < 0 || edgeIndex >= prev.length) return prev;
      return resizeEdgeKeepStart(prev, edgeIndex, newLen, snapGrid);
    });
    setShapeMode("polygon");
    markDirty();
  }, [editingSpan, drawing, snapGrid, markDirty]);

  const openSpanEdit = useCallback(
    (label, event) => {
      if (label.preview || label.edgeIndex == null) return;
      event.preventDefault();
      event.stopPropagation();
      setContextMenu(null);
      setEditingSpan({
        edgeIndex: label.edgeIndex,
        value: String(Number(label.length.toFixed(3))),
        x: label.x,
        y: label.y,
      });
    },
    []
  );

  const onBackgroundFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBgUploadError("");
    try {
      const bg = await loadImageFileAsBackground(file);
      setBackgroundImage(bg);
      markDirty();
    } catch (err) {
      setBgUploadError(err.message || "Could not load image.");
    }
  };

  const hitThreshold = Math.max(viewBox.width, viewBox.height) * 0.03;
  const zoomPct = Math.round((WORLD_WIDTH_FT / viewBox.width) * 100);

  const spanLabels = useMemo(() => {
    if (!showSpanLabels) return [];
    return buildSpanLabels(activePolygon, {
      closed: !drawing && activePolygon.length >= 3,
      previewTo: drawing ? cursorWorld : null,
    });
  }, [showSpanLabels, activePolygon, drawing, cursorWorld]);

  const clientToWorld = useCallback(
    (clientX, clientY, { applySnap = true } = {}) => {
      const svg = svgRef.current;
      if (!svg) return null;
      const ctm = svg.getScreenCTM();
      if (!ctm) return null;
      const pt = svg.createSVGPoint();
      pt.x = clientX;
      pt.y = clientY;
      const world = pt.matrixTransform(ctm.inverse());
      const clamped = clampPointToWorld(world);
      if (!applySnap) return clamped;
      return clampPointToWorld({
        x: snap(clamped.x, snapGrid),
        y: snap(clamped.y, snapGrid),
      });
    },
    [snapGrid]
  );

  const onSvgWheel = useCallback(
    (event) => {
      event.preventDefault();
      const anchor = clientToWorld(event.clientX, event.clientY, { applySnap: false });
      if (!anchor) return;

      const factor = event.deltaY > 0 ? 1.12 : 1 / 1.12;
      setViewBox((prev) => {
        const nextWidth = clamp(prev.width * factor, MIN_VIEW_WIDTH_FT, MAX_VIEW_WIDTH_FT);
        const nextHeight = nextWidth / WORLD_ASPECT;
        const relX = (anchor.x - prev.minX) / prev.width;
        const relY = (anchor.y - prev.minY) / prev.height;
        return clampViewBox({
          minX: anchor.x - relX * nextWidth,
          minY: anchor.y - relY * nextHeight,
          width: nextWidth,
          height: nextHeight,
        });
      });
    },
    [clientToWorld]
  );

  useEffect(() => {
    const el = svgWrapRef.current;
    if (!el) return undefined;
    const handler = (event) => onSvgWheel(event);
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [onSvgWheel]);

  const resetZoom = useCallback(() => {
    setViewBox(INITIAL_VIEWBOX);
  }, []);

  const applyRectangle = useCallback((w, d) => {
    const width = Math.max(1, Number(w) || 0);
    const depth = Math.max(1, Number(d) || 0);
    setRoomW(width);
    setRoomD(depth);
    setPolygon(rectanglePolygon(width, depth));
    setDrawing(false);
    setDraftPoints([]);
    setDirty(true);
  }, []);

  const startPolygonDraw = () => {
    setShapeMode("polygon");
    setDrawing(true);
    setDraftPoints([]);
  };

  const closePolygon = () => {
    if (draftPoints.length < 3) return;
    setPolygon(clonePoints(draftPoints));
    setDrawing(false);
    setDraftPoints([]);
    setShapeMode("polygon");
  };

  const cancelDraw = () => {
    setDrawing(false);
    setDraftPoints([]);
  };

  const resetRectangle = () => {
    setShapeMode("rectangle");
    applyRectangle(DEFAULT_ROOM_W, DEFAULT_ROOM_D);
  };

  const removeDraftVertex = (index) => {
    setDraftPoints((prev) => {
      const next = prev.filter((_, i) => i !== index);
      draftPointsRef.current = next;
      return next;
    });
  };

  const removePolygonVertex = (index) => {
    setPolygon((prev) => {
      if (prev.length <= 3) return prev;
      return prev.filter((_, i) => i !== index);
    });
    setShapeMode("polygon");
  };

  const onSvgClick = (event) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    if (event.button != null && event.button !== 0) return;
    if (dragVertex != null) return;
    if (editingSpan) {
      commitSpanEdit();
      return;
    }
    const pt = clientToWorld(event.clientX, event.clientY);
    if (!pt) return;

    if (drawing) {
      setDraftPoints((prev) => {
        const next = [...prev, pt];
        draftPointsRef.current = next;
        return next;
      });
      return;
    }

    if (shapeMode !== "polygon") return;

    // Left-click near an edge inserts a vertex on that span.
    const edgeIndex = findNearestEdgeInsert(pt, polygon, hitThreshold);
    if (edgeIndex >= 0) {
      const a = polygon[edgeIndex];
      const b = polygon[(edgeIndex + 1) % polygon.length];
      const { point } = distPointToSegment(pt, a, b);
      const inserted = {
        x: snap(point.x, snapGrid),
        y: snap(point.y, snapGrid),
      };
      setPolygon((prev) => {
        const next = [...prev];
        next.splice(edgeIndex + 1, 0, inserted);
        return next;
      });
    }
  };

  const closeContextMenu = () => setContextMenu(null);

  const addObstructionAt = useCallback(
    (pt, draft = obsDraft) => {
      const w = Math.max(0.1, Number(draft.w) || 2);
      const h = Math.max(0.1, Number(draft.h) || 2);
      const obs = {
        id: newObstructionId(),
        x: snap(pt.x - w / 2, snapGrid),
        y: snap(pt.y - h / 2, snapGrid),
        w,
        h,
        type: draft.type || "other",
        label: draft.label || "Obstruction",
      };
      setObstructions((prev) => [...prev, clampPointToWorldRect(obs)]);
      markDirty();
    },
    [obsDraft, snapGrid, markDirty]
  );

  const onSvgContextMenu = (event) => {
    event.preventDefault();
    if (dragVertex != null || dragObs != null) return;
    const pt = clientToWorld(event.clientX, event.clientY);
    if (!pt) return;

    const wrap = svgWrapRef.current?.getBoundingClientRect();
    const menuX = wrap ? event.clientX - wrap.left : event.clientX;
    const menuY = wrap ? event.clientY - wrap.top : event.clientY;

    if (drawing) {
      const hit = findNearestVertex(pt, draftPoints, hitThreshold);
      setContextMenu({
        x: menuX,
        y: menuY,
        world: pt,
        items: [
          hit >= 0
            ? { id: "remove-vertex", label: "Remove vertex", action: () => removeDraftVertex(hit) }
            : { id: "undo-vertex", label: "Undo last vertex", action: () => setDraftPoints((p) => p.slice(0, -1)) },
        ],
      });
      return;
    }

    const obsHit = findNearestObstruction(pt, obstructions, hitThreshold);
    if (obsHit >= 0) {
      setContextMenu({
        x: menuX,
        y: menuY,
        world: pt,
        items: [
          {
            id: "remove-obs",
            label: `Remove ${obstructions[obsHit].label || "obstruction"}`,
            action: () => {
              setObstructions((prev) => prev.filter((_, i) => i !== obsHit));
              markDirty();
            },
          },
        ],
      });
      return;
    }

    const vtxHit = findNearestVertex(pt, polygon, hitThreshold);
    const items = [];
    if (vtxHit >= 0 && polygon.length > 3) {
      items.push({
        id: "remove-vertex",
        label: "Remove vertex",
        action: () => removePolygonVertex(vtxHit),
      });
    }
    items.push({
      id: "add-obs",
      label: "Add obstruction here…",
      action: () => addObstructionAt(pt),
    });
    OBSTRUCTION_PRESETS.forEach((preset) => {
      items.push({
        id: `add-${preset.id}`,
        label: `Add ${preset.label} (${preset.w}×${preset.h} ft)`,
        action: () =>
          addObstructionAt(pt, {
            type: preset.id,
            w: preset.w,
            h: preset.h,
            label: preset.label,
          }),
      });
    });
    setContextMenu({ x: menuX, y: menuY, world: pt, items });
  };

  const onSvgDoubleClick = (event) => {
    if (!drawing) return;
    event.preventDefault();
    // Drop the stray vertex from the second click of a double-click, then close if possible.
    const next = draftPointsRef.current.slice(0, -1);
    if (next.length >= 3) {
      setPolygon(clonePoints(next));
      setDrawing(false);
      setDraftPoints([]);
      setShapeMode("polygon");
      return;
    }
    setDraftPoints(next);
  };

  const onVertexPointerDown = (index, event) => {
    if (event.button === 2) {
      event.preventDefault();
      event.stopPropagation();
      suppressClickRef.current = true;
      if (drawing) removeDraftVertex(index);
      else if (shapeMode === "polygon") removePolygonVertex(index);
      return;
    }
    if (drawing) return;
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    setDragVertex(index);
  };

  const endPan = useCallback((event) => {
    const pan = panRef.current;
    if (!pan) return;
    if (event?.pointerId != null && pan.pointerId != null && event.pointerId !== pan.pointerId) {
      return;
    }
    try {
      if (pan.pointerId != null) {
        svgWrapRef.current?.releasePointerCapture?.(pan.pointerId);
      }
    } catch {
      /* already released */
    }
    panRef.current = null;
    setIsPanning(false);
  }, []);

  const onSvgPointerDown = (event) => {
    // Middle mouse button pans the viewport.
    if (event.button !== 1) return;
    event.preventDefault();
    event.stopPropagation();
    closeContextMenu();
    setDragVertex(null);
    setDragObs(null);
    panRef.current = {
      lastX: event.clientX,
      lastY: event.clientY,
      pointerId: event.pointerId,
    };
    setIsPanning(true);
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onSvgPointerMove = (event) => {
    if (panRef.current) {
      event.preventDefault();
      const svg = svgRef.current;
      const ctm = svg?.getScreenCTM?.();
      if (!svg || !ctm) return;
      const inv = ctm.inverse();
      const p0 = svg.createSVGPoint();
      p0.x = panRef.current.lastX;
      p0.y = panRef.current.lastY;
      const p1 = svg.createSVGPoint();
      p1.x = event.clientX;
      p1.y = event.clientY;
      const w0 = p0.matrixTransform(inv);
      const w1 = p1.matrixTransform(inv);
      panRef.current.lastX = event.clientX;
      panRef.current.lastY = event.clientY;
      const prev = viewBoxRef.current;
      const next = clampViewBox({
        ...prev,
        minX: prev.minX + (w0.x - w1.x),
        minY: prev.minY + (w0.y - w1.y),
      });
      viewBoxRef.current = next;
      setViewBox(next);
      return;
    }

    const pt = clientToWorld(event.clientX, event.clientY);
    if (pt) setCursorWorld(pt);
    if (dragObs != null && pt) {
      setObstructions((prev) =>
        prev.map((o, i) =>
          i === dragObs
            ? clampPointToWorldRect({
                ...o,
                x: snap(pt.x - o.w / 2, snapGrid),
                y: snap(pt.y - o.h / 2, snapGrid),
              })
            : o
        )
      );
      markDirty();
      return;
    }
    if (dragVertex == null) return;
    if (!pt) return;
    setPolygon((prev) => prev.map((p, i) => (i === dragVertex ? pt : p)));
    setShapeMode("polygon");
    markDirty();
  };

  const onSvgPointerUp = (event) => {
    if (event?.button === 1 || panRef.current) endPan(event);
    setDragVertex(null);
    setDragObs(null);
  };

  const onSvgPointerLeave = () => {
    if (panRef.current) return;
    setDragVertex(null);
    setDragObs(null);
    setCursorWorld(null);
  };

  const saveLayout = async ({ asNew = false } = {}) => {
    const name = String(savedName || "").trim() || "Untitled layout";
    setSaveBusy(true);
    setSaveError("");
    try {
      const payload = { name, data: collectLayoutData() };
      let layout;
      if (!asNew && savedId) {
        layout = await updateDatacenterLayout(savedId, payload, auth);
      } else {
        layout = await createDatacenterLayout(payload, auth);
      }
      setSavedId(layout.id);
      setSavedName(layout.name);
      setDirty(false);
      await refreshSavedList();
    } catch (err) {
      setSaveError(err?.response?.data?.error || err.message || "Save failed.");
    } finally {
      setSaveBusy(false);
    }
  };

  const loadSavedLayout = async (id) => {
    if (!id) {
      setSavedId(null);
      setSavedName("Untitled layout");
      return;
    }
    setSaveBusy(true);
    setSaveError("");
    try {
      const layout = await getDatacenterLayout(id, auth);
      if (!layout) throw new Error("Layout not found.");
      setSavedId(layout.id);
      setSavedName(layout.name || "Untitled layout");
      applyLayoutData(layout.data);
    } catch (err) {
      setSaveError(err?.response?.data?.error || err.message || "Could not open layout.");
    } finally {
      setSaveBusy(false);
    }
  };

  const removeSavedLayout = async () => {
    if (!savedId) return;
    if (!window.confirm("Delete this saved layout?")) return;
    setSaveBusy(true);
    setSaveError("");
    try {
      await deleteDatacenterLayout(savedId, auth);
      setSavedId(null);
      setSavedName("Untitled layout");
      await refreshSavedList();
    } catch (err) {
      setSaveError(err?.response?.data?.error || err.message || "Could not delete layout.");
    } finally {
      setSaveBusy(false);
    }
  };

  const updateVertex = (index, axis, value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return;
    setPolygon((prev) => prev.map((p, i) => (i === index ? { ...p, [axis]: n } : p)));
    setShapeMode("polygon");
  };

  const removeVertex = (index) => {
    removePolygonVertex(index);
  };

  const roomLabel =
    shapeMode === "rectangle"
      ? `${formatFeet(roomW, 1)} × ${formatFeet(roomD, 1)}`
      : `${activePolygon.length} vertices`;

  const pointsAttr = activePolygon.map((p) => `${p.x},${p.y}`).join(" ");

  return (
    <div className="dc-shell">
      <div className="dc-page">
        <header className="dc-header">
          <div>
            <h1 className="dc-title">Datacenter layout</h1>
            <p className="dc-lead">
              Fiber data-center planner for Network Managers. Draw the room, set clearances, right-click
              to place pillars/vents, then save the layout for later edits.
            </p>
          </div>
          <div className="dc-stats" aria-live="polite">
            <div className="dc-stat">
              <span className="dc-stat-label">Racks</span>
              <span className="dc-stat-value">{layout.racks?.length ?? layout.count}</span>
            </div>
            <div className="dc-stat">
              <span className="dc-stat-label">Room area</span>
              <span className="dc-stat-value">{layout.roomArea.toFixed(0)} ft²</span>
            </div>
            <div className="dc-stat">
              <span className="dc-stat-label">Utilization</span>
              <span className="dc-stat-value">{(layout.utilization * 100).toFixed(1)}%</span>
            </div>
            <div className="dc-stat">
              <span className="dc-stat-label">Pattern</span>
              <span className="dc-stat-value">
                {layout.pattern === "hotCold" ? "Hot/cold" : "Single"}
              </span>
            </div>
          </div>
        </header>

        <div className="dc-layout">
          <aside className="dc-controls">
            <div>
              <h2 className="dc-section-title">Saved layouts</h2>
              <div className="dc-field-grid" style={{ marginTop: "0.45rem" }}>
                <div className="dc-field dc-field--full">
                  <label htmlFor="dc-saved-select">Open</label>
                  <select
                    id="dc-saved-select"
                    value={savedId || ""}
                    disabled={saveBusy}
                    onChange={(e) => loadSavedLayout(e.target.value || null)}
                  >
                    <option value="">— New unsaved layout —</option>
                    {savedList.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                        {l.updatedAt ? ` · ${new Date(l.updatedAt).toLocaleDateString()}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="dc-field dc-field--full">
                  <label htmlFor="dc-saved-name">Name</label>
                  <input
                    id="dc-saved-name"
                    type="text"
                    value={savedName}
                    onChange={(e) => {
                      setSavedName(e.target.value);
                      markDirty();
                    }}
                  />
                </div>
              </div>
              <div className="dc-actions" style={{ marginTop: "0.55rem" }}>
                <button
                  type="button"
                  className="dc-btn dc-btn--primary"
                  disabled={saveBusy}
                  onClick={() => saveLayout({ asNew: false })}
                >
                  {savedId ? (dirty ? "Save changes" : "Saved") : "Save layout"}
                </button>
                {savedId ? (
                  <button
                    type="button"
                    className="dc-btn"
                    disabled={saveBusy}
                    onClick={() => saveLayout({ asNew: true })}
                  >
                    Save as new
                  </button>
                ) : null}
                {savedId ? (
                  <button
                    type="button"
                    className="dc-btn dc-btn--danger"
                    disabled={saveBusy}
                    onClick={removeSavedLayout}
                  >
                    Delete
                  </button>
                ) : null}
              </div>
              {saveError ? <p className="dc-hint dc-hint--error">{saveError}</p> : null}
              {dirty && savedId ? <p className="dc-hint">Unsaved changes</p> : null}
            </div>

            <div>
              <h2 className="dc-section-title">Room shape</h2>
              <div className="dc-segmented" style={{ marginTop: "0.45rem" }}>
                <button
                  type="button"
                  className={`dc-seg${shapeMode === "rectangle" && !drawing ? " is-active" : ""}`}
                  onClick={() => {
                    setShapeMode("rectangle");
                    applyRectangle(roomW, roomD);
                  }}
                >
                  Rectangle
                </button>
                <button
                  type="button"
                  className={`dc-seg${shapeMode === "polygon" || drawing ? " is-active" : ""}`}
                  onClick={startPolygonDraw}
                >
                  Draw polygon
                </button>
              </div>
            </div>

            {shapeMode === "rectangle" && !drawing ? (
              <>
                <div className="dc-field-grid">
                  <div className="dc-field">
                    <label htmlFor="dc-room-w">Room width (ft)</label>
                    <input
                      id="dc-room-w"
                      type="number"
                      min="1"
                      step="0.5"
                      value={roomW}
                      onChange={(e) => applyRectangle(e.target.value, roomD)}
                    />
                  </div>
                  <div className="dc-field">
                    <label htmlFor="dc-room-d">Room depth (ft)</label>
                    <input
                      id="dc-room-d"
                      type="number"
                      min="1"
                      step="0.5"
                      value={roomD}
                      onChange={(e) => applyRectangle(roomW, e.target.value)}
                    />
                  </div>
                </div>
                <p className="dc-hint">
                  Or click any span length on the plan to type exact footage (keeps the first vertex of
                  that span fixed).
                </p>
              </>
            ) : (
              <>
                <p className="dc-hint">
                  {drawing
                    ? "Left-click to add a vertex. Click a span length to type exact footage. Right-click for remove/undo. Double-click or Close when done (3+ points)."
                    : "Click a span length to set exact footage (moves the next vertex). Left-click an edge to insert a vertex. Right-click to add/remove obstructions."}
                </p>
                <div className="dc-actions">
                  {drawing ? (
                    <>
                      <button
                        type="button"
                        className="dc-btn dc-btn--primary"
                        onClick={closePolygon}
                        disabled={draftPoints.length < 3}
                      >
                        Close polygon
                      </button>
                      <button type="button" className="dc-btn" onClick={cancelDraw}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="dc-btn" onClick={startPolygonDraw}>
                        Redraw
                      </button>
                      <button type="button" className="dc-btn" onClick={resetRectangle}>
                        Reset rectangle
                      </button>
                    </>
                  )}
                </div>
                {!drawing && polygon.length >= 3 ? (
                  <ul className="dc-vertex-list" aria-label="Polygon vertices">
                    {polygon.map((p, i) => (
                      <li key={`v-${i}`}>
                        <span>#{i + 1}</span>
                        <input
                          aria-label={`Vertex ${i + 1} X`}
                          type="number"
                          step={snapGrid || 0.1}
                          value={Number(p.x.toFixed(3))}
                          onChange={(e) => updateVertex(i, "x", e.target.value)}
                        />
                        <input
                          aria-label={`Vertex ${i + 1} Y`}
                          type="number"
                          step={snapGrid || 0.1}
                          value={Number(p.y.toFixed(3))}
                          onChange={(e) => updateVertex(i, "y", e.target.value)}
                        />
                        <button type="button" onClick={() => removeVertex(i)} disabled={polygon.length <= 3}>
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            )}

            <div>
              <h2 className="dc-section-title">Trace background</h2>
              <p className="dc-hint" style={{ marginTop: "0.35rem" }}>
                Upload a floor plan or photo and draw over it. Image is compressed for saving.
              </p>
              <input
                ref={bgFileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                hidden
                onChange={onBackgroundFile}
              />
              <div className="dc-actions" style={{ marginTop: "0.45rem" }}>
                <button
                  type="button"
                  className="dc-btn dc-btn--primary"
                  onClick={() => bgFileRef.current?.click()}
                >
                  {backgroundImage ? "Replace image" : "Upload image"}
                </button>
                {backgroundImage ? (
                  <button
                    type="button"
                    className="dc-btn"
                    onClick={() => {
                      setBackgroundImage(null);
                      markDirty();
                    }}
                  >
                    Clear
                  </button>
                ) : null}
              </div>
              {bgUploadError ? <p className="dc-hint dc-hint--error">{bgUploadError}</p> : null}
              {backgroundImage ? (
                <div className="dc-field-grid" style={{ marginTop: "0.55rem" }}>
                  <div className="dc-field dc-field--full">
                    <label htmlFor="dc-bg-opacity">
                      Opacity ({Math.round(backgroundImage.opacity * 100)}%)
                    </label>
                    <input
                      id="dc-bg-opacity"
                      type="range"
                      min="0.1"
                      max="1"
                      step="0.05"
                      value={backgroundImage.opacity}
                      onChange={(e) => {
                        setBackgroundImage((bg) =>
                          bg ? { ...bg, opacity: Number(e.target.value) } : bg
                        );
                        markDirty();
                      }}
                    />
                  </div>
                  <div className="dc-field">
                    <label htmlFor="dc-bg-w">Width (ft)</label>
                    <input
                      id="dc-bg-w"
                      type="number"
                      min="1"
                      step="1"
                      value={Number(backgroundImage.w.toFixed(2))}
                      onChange={(e) => {
                        const w = Math.max(1, Number(e.target.value) || 1);
                        setBackgroundImage((bg) => {
                          if (!bg) return bg;
                          const aspect = bg.h / bg.w;
                          return { ...bg, w, h: w * aspect };
                        });
                        markDirty();
                      }}
                    />
                  </div>
                  <div className="dc-field">
                    <label className="dc-checkbox-label">
                      <input
                        type="checkbox"
                        checked={backgroundImage.visible !== false}
                        onChange={(e) => {
                          setBackgroundImage((bg) =>
                            bg ? { ...bg, visible: e.target.checked } : bg
                          );
                          markDirty();
                        }}
                      />
                      Show image
                    </label>
                  </div>
                  <div className="dc-field">
                    <label htmlFor="dc-bg-x">Origin X (ft)</label>
                    <input
                      id="dc-bg-x"
                      type="number"
                      step={snapGrid || 1}
                      value={Number(backgroundImage.x.toFixed(2))}
                      onChange={(e) => {
                        setBackgroundImage((bg) =>
                          bg ? { ...bg, x: Number(e.target.value) || 0 } : bg
                        );
                        markDirty();
                      }}
                    />
                  </div>
                  <div className="dc-field">
                    <label htmlFor="dc-bg-y">Origin Y (ft)</label>
                    <input
                      id="dc-bg-y"
                      type="number"
                      step={snapGrid || 1}
                      value={Number(backgroundImage.y.toFixed(2))}
                      onChange={(e) => {
                        setBackgroundImage((bg) =>
                          bg ? { ...bg, y: Number(e.target.value) || 0 } : bg
                        );
                        markDirty();
                      }}
                    />
                  </div>
                </div>
              ) : null}
            </div>

            <div>
              <h2 className="dc-section-title">Obstructions</h2>
              <p className="dc-hint" style={{ marginTop: "0.35rem" }}>
                Right-click the plan to place. Default size for new placements:
              </p>
              <div className="dc-field-grid" style={{ marginTop: "0.45rem" }}>
                <div className="dc-field">
                  <label htmlFor="dc-obs-type">Type</label>
                  <select
                    id="dc-obs-type"
                    value={obsDraft.type}
                    onChange={(e) => {
                      const preset = OBSTRUCTION_PRESETS.find((p) => p.id === e.target.value);
                      setObsDraft((d) => ({
                        ...d,
                        type: e.target.value,
                        label: preset?.label || d.label,
                        w: preset?.w ?? d.w,
                        h: preset?.h ?? d.h,
                      }));
                    }}
                  >
                    {OBSTRUCTION_PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="dc-field">
                  <label htmlFor="dc-obs-w">W × D (ft)</label>
                  <div className="dc-inline-pair">
                    <input
                      id="dc-obs-w"
                      type="number"
                      min="0.25"
                      step="0.25"
                      value={obsDraft.w}
                      onChange={(e) => setObsDraft((d) => ({ ...d, w: Number(e.target.value) }))}
                    />
                    <input
                      aria-label="Obstruction depth"
                      type="number"
                      min="0.25"
                      step="0.25"
                      value={obsDraft.h}
                      onChange={(e) => setObsDraft((d) => ({ ...d, h: Number(e.target.value) }))}
                    />
                  </div>
                </div>
              </div>
              {obstructions.length ? (
                <ul className="dc-obs-list" aria-label="Obstructions">
                  {obstructions.map((o, i) => (
                    <li key={o.id}>
                      <span>
                        {o.label} · {o.w}×{o.h} @ ({o.x.toFixed(1)}, {o.y.toFixed(1)})
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setObstructions((prev) => prev.filter((_, idx) => idx !== i));
                          markDirty();
                        }}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="dc-hint">No obstructions yet.</p>
              )}
            </div>

            <div>
              <h2 className="dc-section-title">Rack</h2>
              <div className="dc-field-grid" style={{ marginTop: "0.45rem" }}>
                <div className="dc-field">
                  <label htmlFor="dc-rack-w">Width (ft)</label>
                  <input
                    id="dc-rack-w"
                    type="number"
                    min="0.5"
                    step="0.1"
                    value={rackW}
                    onChange={(e) => {
                      setRackW(Number(e.target.value));
                      markDirty();
                    }}
                  />
                </div>
                <div className="dc-field">
                  <label htmlFor="dc-rack-d">Depth (ft)</label>
                  <input
                    id="dc-rack-d"
                    type="number"
                    min="0.5"
                    step="0.1"
                    value={rackD}
                    onChange={(e) => {
                      setRackD(Number(e.target.value));
                      markDirty();
                    }}
                  />
                </div>
              </div>
            </div>

            <div>
              <h2 className="dc-section-title">Clearances</h2>
              <div className="dc-field-grid" style={{ marginTop: "0.45rem" }}>
                <div className="dc-field">
                  <label htmlFor="dc-wall">Wall (ft)</label>
                  <input
                    id="dc-wall"
                    type="number"
                    min="0"
                    step="0.25"
                    value={wallClearance}
                    onChange={(e) => setWallClearance(Number(e.target.value))}
                  />
                </div>
                <div className="dc-field">
                  <label htmlFor="dc-cold">Cold aisle (ft)</label>
                  <input
                    id="dc-cold"
                    type="number"
                    min="0"
                    step="0.25"
                    value={coldAisle}
                    onChange={(e) => setColdAisle(Number(e.target.value))}
                  />
                </div>
                <div className="dc-field">
                  <label htmlFor="dc-hot">Hot aisle (ft)</label>
                  <input
                    id="dc-hot"
                    type="number"
                    min="0"
                    step="0.25"
                    value={hotAisle}
                    onChange={(e) => setHotAisle(Number(e.target.value))}
                  />
                </div>
                <div className="dc-field dc-field--full">
                  <label htmlFor="dc-side-preset">Between-rack gap (fiber VCM)</label>
                  <select
                    id="dc-side-preset"
                    value={
                      SIDE_GAP_PRESETS.find((p) => Math.abs(p.ft - sideGap) < 1e-6)?.id || "custom"
                    }
                    onChange={(e) => {
                      const preset = SIDE_GAP_PRESETS.find((p) => p.id === e.target.value);
                      if (preset) setSideGap(preset.ft);
                    }}
                  >
                    {SIDE_GAP_PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                    {!SIDE_GAP_PRESETS.some((p) => Math.abs(p.ft - sideGap) < 1e-6) ? (
                      <option value="custom">Custom ({sideGap} ft)</option>
                    ) : null}
                  </select>
                </div>
                <div className="dc-field">
                  <label htmlFor="dc-side">Side gap (ft)</label>
                  <input
                    id="dc-side"
                    type="number"
                    min="0"
                    step="0.0833"
                    value={sideGap}
                    onChange={(e) => setSideGap(Number(e.target.value))}
                  />
                </div>
                <div className="dc-field">
                  <label>&nbsp;</label>
                  <p className="dc-hint" style={{ marginTop: "0.15rem" }}>
                    Default 6″ VCM. TIA-942 does not require a side gap.
                  </p>
                </div>
              </div>
            </div>

            <div className="dc-field-grid">
              <div className="dc-field">
                <label htmlFor="dc-pattern">Aisle pattern</label>
                <select id="dc-pattern" value={pattern} onChange={(e) => setPattern(e.target.value)}>
                  <option value="auto">Auto (best fit)</option>
                  <option value="hotCold">Hot / cold</option>
                  <option value="single">Single aisle rows</option>
                </select>
              </div>
              <div className="dc-field">
                <label htmlFor="dc-orient">Orientation</label>
                <select
                  id="dc-orient"
                  value={orientation}
                  onChange={(e) => setOrientation(e.target.value)}
                >
                  <option value="auto">Auto (best fit)</option>
                  <option value="x">Aisles east–west</option>
                  <option value="y">Aisles north–south</option>
                </select>
              </div>
              <div className="dc-field dc-field--full">
                <label htmlFor="dc-snap">Draw snap grid (ft)</label>
                <input
                  id="dc-snap"
                  type="number"
                  min="0"
                  step="0.25"
                  value={snapGrid}
                  onChange={(e) => setSnapGrid(Number(e.target.value))}
                />
              </div>
            </div>

            <p className="dc-hint">
              Room: {roomLabel}. Aisles along{" "}
              {layout.aisleAlong === "x" ? "east–west" : "north–south"}. Units are feet (2×4 ft
              rack ≈ 24″×48″). Cold/hot defaults follow TIA-942 preferred aisles (4′ / 3′).
            </p>
          </aside>

          <section className="dc-canvas-panel" aria-label="Floor plan">
            <div className="dc-canvas-toolbar">
              <span className="dc-canvas-mode">
                {drawing
                  ? `Drawing… ${draftPoints.length} point${draftPoints.length === 1 ? "" : "s"}`
                  : `${(layout.racks?.length ?? layout.count) || 0} rack${
                      (layout.racks?.length ?? layout.count) === 1 ? "" : "s"
                    } placed`}
                <span className="dc-canvas-zoom">
                  {" "}
                  · {WORLD_WIDTH_FT}×{WORLD_HEIGHT_FT} ft · {zoomPct}% · scroll zoom · middle-drag pan
                </span>
              </span>
              <div className="dc-canvas-toolbar-right">
                <button type="button" className="dc-btn" onClick={resetZoom} disabled={zoomPct === 100}>
                  Reset zoom
                </button>
                <div className="dc-legend">
                  <span className="dc-legend-item">
                    <span className="dc-swatch dc-swatch--room" /> Room
                  </span>
                  <span className="dc-legend-item">
                    <span className="dc-swatch dc-swatch--front" /> Front-facing
                  </span>
                  <span className="dc-legend-item">
                    <span className="dc-swatch dc-swatch--back" /> Back-facing
                  </span>
                  <span className="dc-legend-item">
                    <span className="dc-swatch dc-swatch--gap" /> Side gap / VCM
                  </span>
                  <span className="dc-legend-item">
                    <span className="dc-swatch dc-swatch--obs" /> Obstruction
                  </span>
                </div>
              </div>
            </div>

            <div
              ref={svgWrapRef}
              className={`dc-svg-wrap${drawing || shapeMode === "polygon" ? "" : " is-idle"}${
                isPanning ? " is-panning" : ""
              }`}
              onPointerDown={onSvgPointerDown}
              onPointerMove={onSvgPointerMove}
              onPointerUp={onSvgPointerUp}
              onPointerLeave={onSvgPointerLeave}
              onPointerCancel={onSvgPointerUp}
              onAuxClick={(e) => {
                if (e.button === 1) e.preventDefault();
              }}
              onClick={closeContextMenu}
            >
              {contextMenu ? (
                <div
                  className="dc-context-menu"
                  style={{ left: contextMenu.x, top: contextMenu.y }}
                  role="menu"
                  onClick={(e) => e.stopPropagation()}
                >
                  {contextMenu.items.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="dc-context-menu-item"
                      role="menuitem"
                      onClick={() => {
                        item.action();
                        closeContextMenu();
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              ) : null}
              <svg
                ref={svgRef}
                className="dc-svg"
                viewBox={`${viewBox.minX} ${viewBox.minY} ${viewBox.width} ${viewBox.height}`}
                preserveAspectRatio="xMidYMid meet"
                onClick={onSvgClick}
                onContextMenu={onSvgContextMenu}
                onDoubleClick={onSvgDoubleClick}
                role="img"
                aria-label="Datacenter floor plan"
              >
                <defs>
                  <pattern id="dc-floor-grid" width="10" height="10" patternUnits="userSpaceOnUse">
                    <path
                      d="M 10 0 L 0 0 0 10"
                      fill="none"
                      stroke="#e2e8f0"
                      strokeWidth="0.12"
                    />
                  </pattern>
                </defs>
                {/* Fixed world canvas */}
                <rect
                  x={0}
                  y={0}
                  width={WORLD_WIDTH_FT}
                  height={WORLD_HEIGHT_FT}
                  fill="#ffffff"
                />
                <rect
                  x={0}
                  y={0}
                  width={WORLD_WIDTH_FT}
                  height={WORLD_HEIGHT_FT}
                  fill="url(#dc-floor-grid)"
                />
                {backgroundImage?.src && backgroundImage.visible !== false ? (
                  <image
                    href={backgroundImage.src}
                    x={backgroundImage.x}
                    y={backgroundImage.y}
                    width={backgroundImage.w}
                    height={backgroundImage.h}
                    opacity={backgroundImage.opacity}
                    preserveAspectRatio="none"
                    style={{ pointerEvents: "none" }}
                  />
                ) : null}
                <rect
                  x={0}
                  y={0}
                  width={WORLD_WIDTH_FT}
                  height={WORLD_HEIGHT_FT}
                  fill="none"
                  stroke="#94a3b8"
                  strokeWidth={Math.max(viewBox.width, viewBox.height) * 0.0009}
                />
                <text
                  x={WORLD_WIDTH_FT / 2}
                  y={Math.max(viewBox.height * 0.028, 3.5)}
                  textAnchor="middle"
                  fill="#64748b"
                  fontSize={Math.max(viewBox.width, viewBox.height) * 0.02}
                  style={{ pointerEvents: "none" }}
                >
                  {WORLD_WIDTH_FT} ft wide
                </text>
                <text
                  x={Math.max(viewBox.width * 0.028, 3.5)}
                  y={WORLD_HEIGHT_FT / 2}
                  textAnchor="middle"
                  fill="#64748b"
                  fontSize={Math.max(viewBox.width, viewBox.height) * 0.02}
                  transform={`rotate(-90 ${Math.max(viewBox.width * 0.028, 3.5)} ${WORLD_HEIGHT_FT / 2})`}
                  style={{ pointerEvents: "none" }}
                >
                  {WORLD_HEIGHT_FT} ft tall
                </text>

                {activePolygon.length >= 2 && !drawing ? (
                  <polygon
                    points={pointsAttr}
                    fill="rgba(148, 163, 184, 0.14)"
                    stroke="#64748b"
                    strokeWidth={Math.max(viewBox.width, viewBox.height) * 0.0012}
                  />
                ) : null}

                {drawing && activePolygon.length >= 2 ? (
                  <polyline
                    points={pointsAttr}
                    fill="none"
                    stroke="#64748b"
                    strokeWidth={Math.max(viewBox.width, viewBox.height) * 0.0012}
                  />
                ) : null}

                {drawing && activePolygon.length >= 1 && cursorWorld ? (
                  <line
                    x1={activePolygon[activePolygon.length - 1].x}
                    y1={activePolygon[activePolygon.length - 1].y}
                    x2={cursorWorld.x}
                    y2={cursorWorld.y}
                    stroke="#38bdf8"
                    strokeWidth={Math.max(viewBox.width, viewBox.height) * 0.0025}
                    strokeDasharray={`${Math.max(viewBox.width, viewBox.height) * 0.01} ${Math.max(viewBox.width, viewBox.height) * 0.007}`}
                  />
                ) : null}

                {draftPoints.length === 1 ? (
                  <circle
                    cx={draftPoints[0].x}
                    cy={draftPoints[0].y}
                    r={Math.max(viewBox.width, viewBox.height) * 0.008}
                    fill="#38bdf8"
                  />
                ) : null}

                {obstructions.map((o, i) => (
                  <g
                    key={o.id}
                    style={{ cursor: "grab" }}
                    onPointerDown={(e) => {
                      if (e.button !== 0) return;
                      e.preventDefault();
                      e.stopPropagation();
                      setDragObs(i);
                      closeContextMenu();
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const wrap = svgWrapRef.current?.getBoundingClientRect();
                      setContextMenu({
                        x: wrap ? e.clientX - wrap.left : e.clientX,
                        y: wrap ? e.clientY - wrap.top : e.clientY,
                        world: { x: o.x, y: o.y },
                        items: [
                          {
                            id: "remove-obs",
                            label: `Remove ${o.label || "obstruction"}`,
                            action: () => {
                              setObstructions((prev) => prev.filter((x) => x.id !== o.id));
                              markDirty();
                            },
                          },
                        ],
                      });
                    }}
                  >
                    <rect
                      x={o.x}
                      y={o.y}
                      width={o.w}
                      height={o.h}
                      fill="rgba(244, 63, 94, 0.55)"
                      stroke="#fb7185"
                      strokeWidth={Math.max(viewBox.width, viewBox.height) * 0.0015}
                    />
                    <text
                      x={o.x + o.w / 2}
                      y={o.y + o.h / 2}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="#fff1f2"
                      fontSize={Math.min(o.w, o.h) * 0.28}
                      fontWeight="700"
                      style={{ pointerEvents: "none" }}
                    >
                      {o.label}
                    </text>
                  </g>
                ))}

                {(layout.sideGaps || []).map((gap) => (
                  <rect
                    key={`gap-${gap.id}`}
                    x={gap.x}
                    y={gap.y}
                    width={gap.w}
                    height={gap.h}
                    fill="rgba(167, 139, 250, 0.45)"
                    stroke="rgba(196, 181, 253, 0.8)"
                    strokeWidth={Math.max(viewBox.width, viewBox.height) * 0.001}
                    style={{ pointerEvents: "none" }}
                  />
                ))}

                {layout.racks.map((rack) => (
                  <g key={rack.id}>
                    <rect
                      x={rack.x}
                      y={rack.y}
                      width={rack.w}
                      height={rack.h}
                      rx={Math.min(rack.w, rack.h) * 0.06}
                      fill={rack.face === "back" ? "#f97316" : "#22d3ee"}
                      fillOpacity={0.88}
                      stroke="#0f172a"
                      strokeWidth={Math.max(viewBox.width, viewBox.height) * 0.0012}
                    />
                    {rack.w * rack.h > 1.5 ? (
                      <text
                        x={rack.x + rack.w / 2}
                        y={rack.y + rack.h / 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="#0f172a"
                        fontSize={Math.min(rack.w, rack.h) * 0.35}
                        fontWeight="700"
                        style={{ pointerEvents: "none", userSelect: "none" }}
                      >
                        {rack.id}
                      </text>
                    ) : null}
                  </g>
                ))}

                {spanLabels.map((label) => {
                  const fontSize = SPAN_LABEL_FONT_FT;
                  const text = formatSpanLabel(label.length);
                  const padX = fontSize * 0.35;
                  const padY = fontSize * 0.28;
                  const approxW = text.length * fontSize * 0.56;
                  const approxH = fontSize * 1.15;
                  const isEditing =
                    !label.preview &&
                    editingSpan &&
                    editingSpan.edgeIndex === label.edgeIndex;
                  return (
                    <g
                      key={label.key}
                      style={{
                        pointerEvents: label.preview ? "none" : "auto",
                        cursor: label.preview ? "default" : "pointer",
                      }}
                      onClick={(e) => openSpanEdit(label, e)}
                      onPointerDown={(e) => {
                        if (!label.preview) e.stopPropagation();
                      }}
                    >
                      <rect
                        x={label.x - approxW / 2 - padX}
                        y={label.y - approxH / 2 - padY * 0.2}
                        width={approxW + padX * 2}
                        height={approxH + padY}
                        rx={fontSize * 0.25}
                        fill={
                          isEditing
                            ? "rgba(14, 116, 144, 0.95)"
                            : label.preview
                              ? "rgba(14, 116, 144, 0.88)"
                              : "rgba(15, 23, 42, 0.82)"
                        }
                        stroke={label.preview || isEditing ? "#67e8f9" : "rgba(148, 163, 184, 0.45)"}
                        strokeWidth={fontSize * 0.06}
                      />
                      {!isEditing ? (
                        <text
                          x={label.x}
                          y={label.y}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="#f8fafc"
                          fontSize={fontSize}
                          fontWeight="700"
                          fontVariantNumeric="tabular-nums"
                          style={{ pointerEvents: "none" }}
                        >
                          {text}
                        </text>
                      ) : null}
                      <title>{label.preview ? "Preview length" : "Click to edit span length"}</title>
                    </g>
                  );
                })}

                {editingSpan ? (
                  <foreignObject
                    x={editingSpan.x - SPAN_EDIT_BOX_W_FT / 2}
                    y={editingSpan.y - SPAN_EDIT_BOX_H_FT / 2}
                    width={SPAN_EDIT_BOX_W_FT}
                    height={SPAN_EDIT_BOX_H_FT}
                    style={{ overflow: "visible" }}
                  >
                    <div
                      xmlns="http://www.w3.org/1999/xhtml"
                      className="dc-span-edit"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <input
                        ref={spanInputRef}
                        type="number"
                        min="0.1"
                        step={snapGrid || 0.1}
                        value={editingSpan.value}
                        aria-label="Span length in feet"
                        onChange={(e) =>
                          setEditingSpan((s) => (s ? { ...s, value: e.target.value } : s))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commitSpanEdit();
                          } else if (e.key === "Escape") {
                            e.preventDefault();
                            setEditingSpan(null);
                          }
                        }}
                        onBlur={() => commitSpanEdit()}
                      />
                      <span>ft</span>
                    </div>
                  </foreignObject>
                ) : null}

                {activePolygon.map((p, i) => (
                  <circle
                    key={`handle-${i}`}
                    cx={p.x}
                    cy={p.y}
                    r={Math.max(viewBox.width, viewBox.height) * 0.01}
                    fill="#f8fafc"
                    stroke="#0e7490"
                    strokeWidth={Math.max(viewBox.width, viewBox.height) * 0.0025}
                    style={{ cursor: drawing ? "crosshair" : "grab" }}
                    onPointerDown={(e) => onVertexPointerDown(i, e)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (drawing) removeDraftVertex(i);
                      else if (shapeMode === "polygon") removePolygonVertex(i);
                    }}
                  />
                ))}

                {!drawing && activePolygon.length >= 3 ? (
                  <text
                    x={roomBounds.minX + roomBounds.width / 2}
                    y={roomBounds.minY - Math.max(viewBox.height * 0.015, 0.8)}
                    textAnchor="middle"
                    fill="#64748b"
                    fontSize={Math.max(viewBox.width, viewBox.height) * 0.02}
                  >
                    Room {formatFeet(roomBounds.width, 1)} × {formatFeet(roomBounds.height, 1)}
                  </text>
                ) : null}
              </svg>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export function DatacenterLayoutGate({ getAuthHeaders }) {
  if (!datacenterLayoutPluginEnabled()) {
    return <Navigate to="/" replace />;
  }
  if (!canAccessDatacenterLayout()) {
    return <Navigate to="/" replace />;
  }
  return <DatacenterPage getAuthHeaders={getAuthHeaders} />;
}

export default DatacenterLayoutGate;
